import { useState } from 'react';
import { useDeals, Deal } from '@/hooks/useDeals';
import { useProducts } from '@/hooks/useProducts';
import { supabase } from '@/lib/supabase';

interface DealsManagerProps {
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

const COLOR_OPTIONS = [
  { label: 'ذهبي', value: '#f59e0b' },
  { label: 'أحمر', value: '#ef4444' },
  { label: 'أخضر', value: '#22c55e' },
  { label: 'وردي', value: '#ec4899' },
  { label: 'سماوي', value: '#06b6d4' },
];

const EMPTY_FORM = {
  title: '',
  subtitle: '',
  product_ids: [] as number[],
  badge_filter: '',
  discount_percent: 0,
  start_at: new Date().toISOString().slice(0, 16),
  end_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
  is_active: true,
  banner_color: '#f59e0b',
};

export default function DealsManager({ onToast }: DealsManagerProps) {
  const { deals, loading, addDeal, updateDeal, deleteDeal } = useDeals();
  const { products } = useProducts();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [sendingNotif, setSendingNotif] = useState<string | null>(null);
  const [notifResult, setNotifResult] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const filteredProducts = products.filter(p =>
    p.nameAr.includes(productSearch) || p.category.toLowerCase().includes(productSearch.toLowerCase())
  );

  const openAdd = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const openEdit = (deal: Deal) => {
    setEditId(deal.id);
    setForm({
      title: deal.title,
      subtitle: deal.subtitle,
      product_ids: deal.product_ids ?? [],
      badge_filter: deal.badge_filter ?? '',
      discount_percent: deal.discount_percent,
      start_at: new Date(deal.start_at).toISOString().slice(0, 16),
      end_at: new Date(deal.end_at).toISOString().slice(0, 16),
      is_active: deal.is_active,
      banner_color: deal.banner_color,
    });
    setShowForm(true);
  };

  const sendDealNotification = async (deal: { title: string; subtitle: string; discount_percent: number; end_at: string }) => {
    setSendingNotif(deal.title);
    setNotifResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('whatsapp-notify', {
        body: {
          type: 'deal_announcement',
          dealTitle: deal.title,
          dealSubtitle: deal.subtitle,
          dealDiscount: deal.discount_percent,
          dealEndAt: deal.end_at,
          dealUrl: `${window.location.origin}/deals`,
        },
      });
      if (error || !data?.success) {
        setNotifResult({ type: 'error', msg: 'فشل الإرسال — تحقق من إعدادات واتساب' });
        onToast('فشل إرسال الإشعار', 'error');
      } else if (data.autoSent) {
        const msg = `تم إرسال الإشعار لـ ${data.sentCount} عميل تلقائياً ✓`;
        setNotifResult({ type: 'success', msg });
        onToast(msg);
      } else {
        // Fallback: open WhatsApp manually for admin
        if (data.adminWhatsAppUrl) window.open(data.adminWhatsAppUrl, '_blank', 'noopener,noreferrer');
        setNotifResult({ type: 'success', msg: 'فُتح واتساب — أضف CALLMEBOT_API_KEY للإرسال التلقائي للعملاء' });
        onToast('فُتح واتساب يدوياً — فعّل CallMeBot للإرسال التلقائي');
      }
    } catch {
      setNotifResult({ type: 'error', msg: 'حدث خطأ أثناء الإرسال' });
      onToast('حدث خطأ أثناء الإرسال', 'error');
    }
    setSendingNotif(null);
    setTimeout(() => setNotifResult(null), 5000);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { onToast('يرجى إدخال عنوان العرض', 'error'); return; }
    if (!form.end_at) { onToast('يرجى تحديد تاريخ انتهاء العرض', 'error'); return; }
    setSaving(true);
    const payload = {
      ...form,
      start_at: new Date(form.start_at).toISOString(),
      end_at: new Date(form.end_at).toISOString(),
    };
    let ok = false;
    const isNew = !editId;
    if (editId) {
      ok = await updateDeal(editId, payload);
    } else {
      ok = await addDeal(payload);
    }
    setSaving(false);
    if (ok) {
      onToast(editId ? 'تم تحديث العرض بنجاح ✓' : 'تم إضافة العرض بنجاح ✓');
      setShowForm(false);
      // Auto-send WhatsApp notification for new active deals
      if (isNew && form.is_active) {
        await sendDealNotification(payload);
      }
    } else {
      onToast('حدث خطأ، حاول مرة أخرى', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const ok = await deleteDeal(deleteId);
    onToast(ok ? 'تم حذف العرض' : 'حدث خطأ أثناء الحذف', ok ? 'success' : 'error');
    setDeleteId(null);
  };

  const toggleProduct = (id: number) => {
    setForm(f => ({
      ...f,
      product_ids: f.product_ids.includes(id)
        ? f.product_ids.filter(x => x !== id)
        : [...f.product_ids, id],
    }));
  };

  const getDealStatus = (deal: Deal) => {
    if (!deal.is_active) return { label: 'معطّل', color: 'text-gray-400 bg-gray-100' };
    const now = new Date();
    if (new Date(deal.start_at) > now) return { label: 'قادم', color: 'text-amber-600 bg-amber-50' };
    if (new Date(deal.end_at) < now) return { label: 'منتهي', color: 'text-red-500 bg-red-50' };
    return { label: 'نشط', color: 'text-green-600 bg-green-50' };
  };

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString('ar-SA', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{deals.length} عرض مضاف</p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 bg-[#1a1a1a] text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-[#333] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          عرض جديد
        </button>
      </div>

      {/* Notification Result Banner */}
      {notifResult && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${
          notifResult.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          <i className={notifResult.type === 'success' ? 'ri-checkbox-circle-line text-green-500' : 'ri-error-warning-line text-red-500'}></i>
          {notifResult.msg}
        </div>
      )}

      {/* Deals List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
        </div>
      ) : deals.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
          <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-2xl mb-4">
            <i className="ri-flashlight-line text-amber-400 text-2xl"></i>
          </div>
          <p className="font-bold text-[#1a1a1a] mb-1">لا توجد عروض بعد</p>
          <p className="text-sm text-gray-400 mb-4">أضف أول عرض لتخفيضاتك</p>
          <button onClick={openAdd} className="bg-[#1a1a1a] text-white px-5 py-2 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap">
            إضافة عرض
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {deals.map(deal => {
            const status = getDealStatus(deal);
            return (
              <div key={deal.id} className="bg-gray-50 rounded-2xl border border-gray-100 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Color dot */}
                    <div className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ background: `${deal.banner_color}22`, border: `2px solid ${deal.banner_color}44` }}>
                      <i className="ri-flashlight-line" style={{ color: deal.banner_color }}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-[#1a1a1a] text-sm truncate">{deal.title}</h3>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${status.color}`}>{status.label}</span>
                        {deal.discount_percent > 0 && (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-500">
                            -{deal.discount_percent}%
                          </span>
                        )}
                      </div>
                      {deal.subtitle && (
                        <p className="text-gray-400 text-xs mt-0.5 truncate">{deal.subtitle}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="text-gray-400 text-xs flex items-center gap-1">
                          <i className="ri-calendar-line text-xs"></i>
                          {formatDate(deal.start_at)}
                        </span>
                        <span className="text-gray-300 text-xs">←</span>
                        <span className="text-gray-400 text-xs flex items-center gap-1">
                          <i className="ri-calendar-check-line text-xs"></i>
                          {formatDate(deal.end_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {deal.product_ids?.length > 0 && (
                          <span className="text-gray-400 text-xs flex items-center gap-1">
                            <i className="ri-shopping-bag-line text-xs"></i>
                            {deal.product_ids.length} منتج محدد
                          </span>
                        )}
                        {deal.badge_filter && (
                          <span className="text-gray-400 text-xs flex items-center gap-1">
                            <i className="ri-price-tag-3-line text-xs"></i>
                            شارة: {deal.badge_filter}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Send WhatsApp Notification */}
                    <button
                      onClick={() => sendDealNotification(deal)}
                      disabled={sendingNotif === deal.title}
                      className="w-8 h-8 flex items-center justify-center bg-green-50 text-green-600 hover:bg-green-100 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                      title="إرسال إشعار واتساب للعملاء"
                    >
                      {sendingNotif === deal.title ? (
                        <i className="ri-loader-4-line animate-spin text-sm"></i>
                      ) : (
                        <i className="ri-whatsapp-line text-sm"></i>
                      )}
                    </button>
                    <button
                      onClick={() => updateDeal(deal.id, { is_active: !deal.is_active }).then(() => onToast(deal.is_active ? 'تم تعطيل العرض' : 'تم تفعيل العرض'))}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                        deal.is_active ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                      title={deal.is_active ? 'تعطيل' : 'تفعيل'}
                    >
                      <i className={deal.is_active ? 'ri-toggle-fill text-sm' : 'ri-toggle-line text-sm'}></i>
                    </button>
                    <button
                      onClick={() => openEdit(deal)}
                      className="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-500 hover:bg-[#1a1a1a] hover:text-white rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setDeleteId(deal.id)}
                      className="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" dir="rtl">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-[#1a1a1a]">{editId ? 'تعديل العرض' : 'إضافة عرض جديد'}</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-6 flex flex-col gap-5">
              {/* Title */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500">عنوان العرض *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="مثال: تخفيضات نهاية الموسم"
                  className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                />
              </div>

              {/* Subtitle */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500">وصف العرض</label>
                <input
                  type="text"
                  value={form.subtitle}
                  onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
                  placeholder="مثال: خصومات تصل إلى 30% على جميع المنتجات"
                  className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                />
              </div>

              {/* Discount + Color */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-gray-500">نسبة الخصم (%)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={form.discount_percent}
                    onChange={e => setForm(f => ({ ...f, discount_percent: Number(e.target.value) }))}
                    className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-gray-500">لون البانر</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {COLOR_OPTIONS.map(c => (
                      <button
                        key={c.value}
                        onClick={() => setForm(f => ({ ...f, banner_color: c.value }))}
                        className={`w-8 h-8 rounded-lg transition-all cursor-pointer ${form.banner_color === c.value ? 'ring-2 ring-offset-2 ring-[#1a1a1a] scale-110' : ''}`}
                        style={{ background: c.value }}
                        title={c.label}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-gray-500">تاريخ البداية</label>
                  <input
                    type="datetime-local"
                    value={form.start_at}
                    onChange={e => setForm(f => ({ ...f, start_at: e.target.value }))}
                    className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-gray-500">تاريخ الانتهاء *</label>
                  <input
                    type="datetime-local"
                    value={form.end_at}
                    onChange={e => setForm(f => ({ ...f, end_at: e.target.value }))}
                    className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                  />
                </div>
              </div>

              {/* Badge Filter */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500">فلتر الشارة (اختياري)</label>
                <input
                  type="text"
                  value={form.badge_filter}
                  onChange={e => setForm(f => ({ ...f, badge_filter: e.target.value }))}
                  placeholder="مثال: خصم — سيعرض كل المنتجات التي تحمل هذه الشارة"
                  className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                />
                <p className="text-gray-400 text-xs">إذا لم تحدد منتجات، سيعرض المنتجات التي تحمل هذه الشارة</p>
              </div>

              {/* Product Selection */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-gray-500">
                  تحديد منتجات العرض ({form.product_ids.length} محدد)
                </label>
                <input
                  type="text"
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  placeholder="بحث عن منتج..."
                  className="border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                />
                <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-xl">
                  {filteredProducts.map(p => (
                    <label
                      key={p.id}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0"
                    >
                      <input
                        type="checkbox"
                        checked={form.product_ids.includes(p.id)}
                        onChange={() => toggleProduct(p.id)}
                        className="w-4 h-4 accent-[#1a1a1a] cursor-pointer"
                      />
                      <img src={p.images?.[0]} alt={p.nameAr} className="w-8 h-8 object-cover rounded-lg" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1a1a1a] truncate">{p.nameAr}</p>
                        <p className="text-xs text-gray-400">{p.price.toLocaleString('ar-SA')} ر.س</p>
                      </div>
                      {p.badge && (
                        <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full shrink-0">{p.badge}</span>
                      )}
                    </label>
                  ))}
                </div>
                {form.product_ids.length === 0 && (
                  <p className="text-gray-400 text-xs">إذا لم تحدد منتجات، سيعرض كل المنتجات التي لها شارة</p>
                )}
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                <div>
                  <p className="text-sm font-medium text-[#1a1a1a]">تفعيل العرض</p>
                  <p className="text-xs text-gray-400 mt-0.5">العرض سيظهر في صفحة العروض</p>
                </div>
                <button
                  onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${form.is_active ? 'bg-[#1a1a1a]' : 'bg-gray-200'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.is_active ? 'left-5' : 'left-0.5'}`} />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowForm(false)}
                className="flex-1 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                إلغاء
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-[#1a1a1a] text-white rounded-xl py-2.5 text-sm font-bold hover:bg-[#333] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <i className="ri-loader-4-line animate-spin"></i>}
                {editId ? 'حفظ التعديلات' : 'إضافة العرض'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" dir="rtl">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-[#1a1a1a] text-center mb-2">حذف العرض</h3>
            <p className="text-sm text-gray-500 text-center mb-6">هل أنت متأكد من حذف هذا العرض؟</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                إلغاء
              </button>
              <button onClick={handleDelete} className="flex-1 bg-red-500 text-white rounded-xl py-2.5 text-sm font-bold hover:bg-red-600 cursor-pointer whitespace-nowrap">
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
